import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Wallet } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import {
  AuthShell,
  AuthField,
  AuthPrimaryButton,
  AuthErrorBanner,
  formatAuthErrorDetail,
} from "@/components/auth/AuthShell";

/**
 * Sign up = email/password + a wallet-first prompt. The wallet address is
 * captured at step 1 so a new user's first invoice is send-ready in about a
 * minute (no dashboard back-and-forth to set a payout address).
 *
 * Carries a `?ref=` referral code through so the on-signup referral
 * attribution keeps working for email accounts too.
 */
export default function Signup() {
  const { login: googleLogin, signupWithPassword } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const referralCode =
    new URLSearchParams(location.search).get("ref") || undefined;

  const [wallet, setWallet] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await signupWithPassword(
        email.trim(),
        password,
        name.trim() || undefined,
        referralCode,
        wallet.trim() || undefined,
      );
      toast.success("Welcome to Quill.");
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(
        formatAuthErrorDetail(err.response?.data?.detail) ||
          "Signup failed. Please try again."
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell
      eyebrow="Get started"
      title="Create your ledger."
      subtitle="Paste your wallet, then send your first invoice in under a minute — all non-custodial."
      footer={
        <>
          Already have an account?{" "}
          <Link
            to="/login"
            data-testid="signup-login-link"
            className="text-[#E8B96C] underline underline-offset-4 decoration-[#E8B96C]/40 hover:decoration-[#E8B96C]"
          >
            Sign in
          </Link>
        </>
      }
    >
      <AuthErrorBanner error={error} testId="signup-error" />

      <form onSubmit={onSubmit} className="space-y-5">
        <div className="rounded-[6px] border border-[#E8B96C]/25 bg-[#E8B96C]/[0.04] p-4">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-[#E8B96C] mb-2">
            <Wallet size={13} strokeWidth={2.2} />
            Step 1 — payout wallet
          </div>
          <AuthField
            label="Wallet address"
            value={wallet}
            onChange={setWallet}
            placeholder="0x… (Ethereum) or bc1… / 1… / 3… (Bitcoin)"
            autoComplete="off"
            required={false}
            testId="signup-wallet-input"
            hint="Where your clients' payments land. You can override this per invoice later — skip if you're not sure yet."
          />
        </div>

        <div className="pt-1 text-[10px] uppercase tracking-[0.22em] text-[#7A6A54]">
          Step 2 — account
        </div>
        <AuthField
          label="Your name"
          value={name}
          onChange={setName}
          placeholder="Optional — appears on your invoices"
          autoComplete="name"
          required={false}
          testId="signup-name-input"
        />
        <AuthField
          label="Email"
          type="email"
          value={email}
          onChange={setEmail}
          placeholder="you@studio.com"
          autoComplete="email"
          testId="signup-email-input"
        />
        <AuthField
          label="Password"
          type="password"
          value={password}
          onChange={setPassword}
          placeholder="At least 8 characters"
          autoComplete="new-password"
          testId="signup-password-input"
          hint="Min 8 chars. We hash it with bcrypt — we never see it."
        />
        <AuthPrimaryButton disabled={busy} testId="signup-submit-button">
          {busy ? "Creating your ledger…" : "Create account"}
        </AuthPrimaryButton>
      </form>

      <div className="my-7 flex items-center gap-3 text-[10px] uppercase tracking-[0.2em] text-[#7A6A54]">
        <div className="flex-1 h-px bg-[#E8B96C]/15" />
        or
        <div className="flex-1 h-px bg-[#E8B96C]/15" />
      </div>

      <button
        type="button"
        onClick={googleLogin}
        data-testid="signup-google-button"
        className="w-full py-3 flex items-center justify-center gap-3 border border-[#E8B96C]/25 rounded-[4px] text-[#F4EBDC] hover:border-[#E8B96C]/50 hover:bg-[#E8B96C]/[0.04] transition-all text-[14px]"
      >
        <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
          <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
          <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
          <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
          <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
        </svg>
        Continue with Google
      </button>

      <p className="mt-6 text-[11px] leading-relaxed text-[#7A6A54] text-center">
        By continuing you agree to Quill's{" "}
        <Link to="/terms" className="text-[#A89680] hover:text-[#E8B96C] underline underline-offset-2 decoration-transparent hover:decoration-[#E8B96C]/40 transition-all">
          Terms
        </Link>{" "}
        &{" "}
        <Link to="/privacy" className="text-[#A89680] hover:text-[#E8B96C] underline underline-offset-2 decoration-transparent hover:decoration-[#E8B96C]/40 transition-all">
          Privacy
        </Link>
        .
      </p>
    </AuthShell>
  );
}
